import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import {
	Alert,
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Grid,
	Link,
	Snackbar,
	Tooltip,
	Typography,
} from "@mui/material"
import { useTheme } from "@mui/material/styles"
import React, { FormEvent, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router"
import { useSearchParams } from "react-router-dom"

import { ReactComponent as ResetSvg } from "../../../assets/img/password.svg"
import { AppDispatch } from "../../../store"
import { changePassword } from "../../../store/actions/recover"
import { clearError } from "../../../store/auth/auth-slice"
import InputPassword from "../../common/input-password"
import PasswordStrenght from "../../common/password-strenght"
import LanguageSelector from "../../langSelector"

const ChangePasswordForm = () => {
	const dispatch = useDispatch<AppDispatch>()
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const resetToken: string | undefined =
		searchParams.get("resetToken") ?? undefined
	const info = resetToken && JSON.parse(atob(resetToken?.split(".")?.[1]))
	const { t } = useTranslation()
	// eslint-disable-next-line
	const [password, setPassword] = useState<string | undefined>(undefined)
	const [passwordStrenghtLevel, setPasswordStrenghtLevel] = useState<number>(0)
	const [ruleMinimumCharLength, setRuleMinimumCharLength] =
		useState<boolean>(false)
	const [isChanged, setIsChanged] = useState<boolean>(false)
	const [ruleCaseSensitive, setRuleCaseSensitive] = useState<boolean>(false)
	const [ruleSpecialChar, setRuleSpecialChar] = useState<boolean>(false)
	const [ruleNumber, setRuleNumber] = useState<boolean>(false)
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [errorMessage, setErrorMessage] = useState<string>("")
	const [isErrorSnackBarOpen, setIsErrorSnackBarOpen] = useState<boolean>(false)

	const theme = useTheme()

	const clearForm = () => {
		// eslint-disable-next-line
		setPassword(undefined)
	}

	useEffect(() => {
		const regexpLowerUpperCase = /^(?=.*[a-z])(?=.*[A-Z]).+$/
		const regexpSpecialChar = /[^\d\sA-Za-z]/
		const regexNumber = /\d/
		const passwordToCheck: string = password ?? ""

		setRuleMinimumCharLength(passwordToCheck.length >= 8)
		setRuleCaseSensitive(regexpLowerUpperCase.test(passwordToCheck))
		setRuleSpecialChar(regexpSpecialChar.test(passwordToCheck))
		setRuleNumber(regexNumber.test(passwordToCheck))
	}, [password])

	useEffect(() => {
		const strengthLevel = [
			ruleMinimumCharLength,
			ruleCaseSensitive,
			ruleSpecialChar,
			ruleNumber,
		].filter((value) => value === true).length
		setPasswordStrenghtLevel(strengthLevel)
	}, [ruleMinimumCharLength, ruleCaseSensitive, ruleSpecialChar, ruleNumber])

	useEffect(() => {
		if (
			errorMessage &&
			!["error.email.invalid", "user.missing.credential"].includes(errorMessage)
		)
			setIsErrorSnackBarOpen(true)
	}, [errorMessage])

	const handleChange = (event: SubmitEvent) => {
		event.preventDefault()
		setErrorMessage("")
		setIsLoading(true)
		dispatch(
			changePassword({
				email: info?.email,
				username: info?.username,
				newPassword: password,
				newPasswordConfirmation: password,
				token: resetToken,
			})
		)
			.then((response) => {
				setErrorMessage(
					response?.payload?.message !== "No message available"
						? response?.payload?.message
						: "error.generic"
				)
				setIsLoading(false)
				setIsChanged(response?.payload?.message === "ok")
			})
			.catch(() => {
				setIsLoading(false)
				setErrorMessage("error.generic")
			})
	}

	return (
		<>
			<Grid
				container
				direction="row"
				padding={"8px 16px"}
				flexWrap={"wrap"}
				alignItems={"center"}
				justifyContent={"space-between"}
				sx={{ maxWidth: "1920px" }}
			>
				<Grid sx={{ alignSelf: "center" }}>
					<Link
						href="https://plick.eu/"
						target="_blank"
						rel="noopener"
						underline="none"
						color="inherit"
						aria-label={t("accessibilityLabel", { ns: "copyright" })}
					>
						<img
							alt="plick logo"
							style={{ height: "30px" }}
							src="data:image/png;base64,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"
						/>
					</Link>
				</Grid>
				<Grid sx={{ color: "#FFF" }}>
					<LanguageSelector />
				</Grid>
			</Grid>
			<Grid
				container
				direction="row"
				flexWrap={"wrap"}
				alignItems={"center"}
				justifyContent={"space-around"}
				minHeight={"485px"}
				width={"100%"}
				sx={{ maxWidth: "1920px" }}
			>
				<Grid
					id="browserImg"
					item
					md={6}
					xs={0}
					style={{
						maxWidth: "calc(100% - 485px)",
						color: theme.palette.primary.main,
					}}
				>
					<ResetSvg style={{ width: "90%" }} />
				</Grid>
				<Grid
					item
					md={6}
					xs={12}
					style={{
						maxWidth: "485px",
						padding: "42px",
						backgroundColor: "#FFF",
						boxShadow: "rgba(0, 0, 0, 0.35) 0px 5px 15px",
					}}
				>
					<Link
						component="button"
						variant="body2"
						underline="none"
						sx={{ display: "flex", marginBottom: "24px" }}
						onClick={() => {
							dispatch(clearError())
							navigate("/login")
						}}
					>
						<ArrowBackIcon sx={{ marginRight: "25px" }} />{" "}
						{t("backToLogin", { ns: "changePasswordForm" })}
					</Link>

					<h1
						style={{
							backgroundColor: "#FFF",
							fontSize: "18px",
							marginBottom: "32px",
							color: "#1C334C",
							fontWeight: 600,
						}}
					>
						{isChanged ? (
							<>
								<div style={{ marginBottom: "24px" }}>
									<CheckCircleOutlineIcon
										sx={{
											fontSize: "64px",
											color: theme.palette.secondary.main,
										}}
									/>
								</div>
								<Typography component="span" variant="body1">
									<b>{t("recover.done.title", { ns: "changePasswordForm" })}</b>
								</Typography>
								<div style={{ marginTop: "16px" }} id="changePasswordForm">
									<Typography component="span" variant="body2">
										{t("recover.done.subtitle", { ns: "changePasswordForm" })}
									</Typography>
								</div>
							</>
						) : (
							t("title", { ns: "changePasswordForm" })
						)}
					</h1>
					{isChanged ? undefined : (
						<>
							<div style={{ marginBottom: "24px" }}>
								{t("subtitle", { ns: "changePasswordForm" })}
							</div>

							<form
								id="changeForm"
								onSubmit={(event: FormEvent) => {
									event.preventDefault()
									handleChange(event)
								}}
							>
								<Grid container spacing={0} direction="column" gap={"16px"}>
									<Grid item md={12} xs={12}>
										<InputPassword
											id="password"
											label={t("password", { ns: "changePasswordForm" })}
											onChange={(event: React.ChangeEvent) => {
												const { target } = event
												if (target)
													setPassword((target as HTMLButtonElement).value || "")
											}}
											variant="outlined"
											value={password ?? ""}
											fullWidth={true}
										/>
										<PasswordStrenght
											passwordStrenght={passwordStrenghtLevel}
										/>
										<ul
											style={{
												listStyle: "none",
												padding: 0,
											}}
										>
											<li>
												<FormControlLabel
													checked={ruleMinimumCharLength}
													control={
														<Checkbox defaultChecked={ruleMinimumCharLength} />
													}
													label={t("rules.minimumCharLength", {
														ns: "changePasswordForm",
													})}
												/>
											</li>
											<li>
												<FormControlLabel
													checked={ruleCaseSensitive}
													control={
														<Checkbox defaultChecked={ruleCaseSensitive} />
													}
													label={t("rules.caseSensitive", {
														ns: "changePasswordForm",
													})}
												/>
											</li>
											<li>
												<FormControlLabel
													checked={ruleSpecialChar}
													control={
														<Checkbox defaultChecked={ruleSpecialChar} />
													}
													label={t("rules.specialChar", {
														ns: "changePasswordForm",
													})}
												/>
											</li>
											<li>
												<FormControlLabel
													checked={ruleNumber}
													control={<Checkbox defaultChecked={ruleNumber} />}
													label={t("rules.number", {
														ns: "changePasswordForm",
													})}
												/>
											</li>
										</ul>
									</Grid>
								</Grid>
							</form>

							<Grid
								container
								direction="row"
								sx={{ marginTop: "32px", width: "100%" }}
								flexGrow={1}
								justifyContent={"space-between"}
							>
								{isLoading ? (
									<Grid item xs={12} sx={{ textAlign: "center" }}>
										<CircularProgress size={32} sx={{}} />
									</Grid>
								) : (
									<>
										<Grid item xs={6}>
											<Button
												variant="outlined"
												id="userSignInReset"
												type="button"
												onClick={() => clearForm()}
											>
												{t("buttons.reset", { ns: "changePasswordForm" })}
											</Button>
										</Grid>
										<Grid item xs={"auto"} justifySelf={"flex-end"}>
											<Tooltip
												enterTouchDelay={0}
												describeChild
												title={
													passwordStrenghtLevel < 4 &&
													t("buttons.tooltipDisabledMessage", {
														ns: "changePasswordForm",
													})
												}
												arrow
											>
												<span>
													<Button
														disabled={passwordStrenghtLevel < 4}
														variant="contained"
														id="userSignInSubmit"
														type="submit"
														form="changeForm"
													>
														{t("buttons.send", { ns: "changePasswordForm" })}
													</Button>
												</span>
											</Tooltip>
										</Grid>
									</>
								)}
							</Grid>
						</>
					)}
				</Grid>
			</Grid>
			{!!errorMessage &&
				!["ok", "error.email.invalid", "user.missing.credential"].includes(
					errorMessage
				) && (
					<Snackbar
						sx={{ width: "calc(100% - 50px)", zIndex: 999 }}
						anchorOrigin={{ vertical: "top", horizontal: "left" }}
						open={isErrorSnackBarOpen}
						autoHideDuration={20000}
						onClose={() => setIsErrorSnackBarOpen(false)}
					>
						<Alert
							onClose={() => setIsErrorSnackBarOpen(false)}
							severity="error"
							variant="filled"
							sx={{ width: "100%" }}
						>
							{t(errorMessage, { ns: "changePasswordForm" })}
						</Alert>
					</Snackbar>
				)}
		</>
	)
}

export default ChangePasswordForm
